<script lang="ts" setup>
import { useField } from 'vee-validate'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxLength: {
    type: Number,
    required: false,
    default: 255,
  },
})

const { value, errorMessage } = useField(() => props.name)
const showPassword = ref(false)
const passwordField = ref()

const timeout = ref()

onBeforeUnmount(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})

const trimErrorMessage = computed(() => {
  return errorMessage.value?.trim()
})

const onClear = () => {
  value.value = ''
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
  timeout.value = setTimeout(() => {
    passwordField.value.blur()
  })
}

const { isMobile } = useDevice()
</script>

<template>
  <div class="form-input">
    <label class="form-input__label" :for="props.name">
      {{ props.label }}
      <span v-if="props.required" class="form-input__required">*</span>
    </label>
    <VTextField
      ref="passwordField"
      v-model="value"
      variant="outlined"
      clearable
      density="compact"
      hide-details
      :class="{
        'form-input__password': true,
        'form-input__password--mobile': isMobile,
      }"
      :type="showPassword ? 'text' : 'password'"
      :placeholder="placeholder"
      :append-inner-icon="showPassword ? 'icon-eye' : 'icon-eye-slash'"
      :error="Boolean(trimErrorMessage)"
      :maxlength="props.maxLength"
      @click:clear="onClear"
      @click:append-inner="togglePasswordVisibility"
    >
      <template #clear>
        <i class="icon-delete" @click="onClear" />
      </template>
    </VTextField>
    <span v-if="trimErrorMessage" class="form-input__error-message">{{
      trimErrorMessage
    }}</span>
  </div>
</template>

<style scoped lang="scss">
@import '~/assets/styles/components/form/input-password.scss';
</style>
